import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  BarChart3,
  DollarSign,
  FileDown,
  Search,
  TrendingUp,
  Building2,
  Users,
  Layers,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { fetchCentrosCostos } from '../centro-costos/services/centroCostos.services';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const CATEGORIAS = ['Nómina', 'Arriendo', 'Servicios Públicos', 'Mantenimiento', 'Administrativo', 'Otro'];

const NIVEL_CONFIG = {
  empresa: { label: 'Empresa', color: 'bg-violet-500', light: 'bg-violet-500/15', text: 'text-violet-700' },
  centro:  { label: 'Centro',  color: 'bg-blue-500',   light: 'bg-blue-500/15',   text: 'text-blue-700' },
  cliente: { label: 'Cliente', color: 'bg-emerald-500', light: 'bg-emerald-500/15', text: 'text-emerald-700' },
};

const CAT_COLORS = [
  'bg-orange-500', 'bg-cyan-500', 'bg-pink-500',
  'bg-amber-500', 'bg-indigo-500', 'bg-teal-500',
];

const fmt = (v) => v != null ? `$${Math.round(Number(v)).toLocaleString('es-CO')}` : '—';
const pct = (part, total) => total > 0 ? Math.round((part / total) * 100) : 0;

const fetchCentrosActivos = async () => {
  const res = await fetch(`${API_URL}/laundry/filter`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ limit: 200, offset: 1, query: { estado: 'Activo' } }),
  });
  if (!res.ok) throw new Error('Error al cargar centros');
  const json = await res.json();
  return json.data || [];
};

const fetchClientesDeCentro = async (centroId) => {
  const res = await fetch(`${API_URL}/client/filter`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ limit: 500, offset: 1, query: { estado: 'Activo', 'lavanderia.id': centroId } }),
  });
  if (!res.ok) throw new Error('Error al cargar clientes');
  const json = await res.json();
  return json.data || [];
};

const BarRow = ({ label, value, total, colorClass, badge }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between text-sm">
      <span className="font-medium text-foreground truncate">{label}</span>
      <div className="flex items-center gap-2 shrink-0 ml-2">
        {badge}
        <span className="font-bold tabular-nums">{fmt(value)}</span>
        <span className="text-muted-foreground w-9 text-right">{pct(value, total)}%</span>
      </div>
    </div>
    <div className="h-2 bg-muted rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${colorClass}`}
        style={{ width: `${pct(value, total)}%` }}
      />
    </div>
  </div>
);

const ReportesFinancierosPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString().slice(0, 10);

  const [fechaDesde, setFechaDesde] = useState(firstOfMonth);
  const [fechaHasta, setFechaHasta] = useState(today);
  const [filterNivel, setFilterNivel] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  const [filterCentroId, setFilterCentroId] = useState('');
  const [filterClienteId, setFilterClienteId] = useState('');

  const [centros, setCentros] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loadingCentros, setLoadingCentros] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(false);

  const [data, setData] = useState([]);
  // Filtros activos en el momento de la búsqueda (para calcular costos proporcionales)
  const [activeSearch, setActiveSearch] = useState({ centroId: '', clienteId: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    setLoadingCentros(true);
    fetchCentrosActivos()
      .then(setCentros)
      .catch(() => {})
      .finally(() => setLoadingCentros(false));
  }, []);

  useEffect(() => {
    if (!filterCentroId) {
      setClientes([]);
      setFilterClienteId('');
      return;
    }
    setLoadingClientes(true);
    setFilterClienteId('');
    fetchClientesDeCentro(filterCentroId)
      .then(setClientes)
      .catch(() => setClientes([]))
      .finally(() => setLoadingClientes(false));
  }, [filterCentroId]);

  const handleSearch = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = {};
      if (filterCategoria) query.categoria = filterCategoria;
      if (fechaDesde) query.fechaDesde = fechaDesde;
      if (fechaHasta) query.fechaHasta = fechaHasta;
      if (filterCentroId) query.centroId = filterCentroId;
      if (filterClienteId) query.clienteId = filterClienteId;

      const result = await fetchCentrosCostos({ limit: 1000, offset: 1, query });
      setData(result.data || []);
      setActiveSearch({ centroId: filterCentroId, clienteId: filterClienteId });
      setHasSearched(true);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [fechaDesde, fechaHasta, filterCategoria, filterCentroId, filterClienteId, toast]);

  // Filtro jerárquico de nivel (solo frontend):
  //   empresa → todos  |  centro → centro+cliente  |  cliente → solo cliente
  const filteredData = useMemo(() => {
    if (!filterNivel || filterNivel === '_all' || filterNivel === 'empresa') return data;
    if (filterNivel === 'centro') return data.filter(r => r.nivel === 'centro' || r.nivel === 'cliente');
    if (filterNivel === 'cliente') return data.filter(r => r.nivel === 'cliente');
    return data;
  }, [data, filterNivel]);

  // Costo efectivo por registro según filtros activos:
  //   - clienteId activo → costo proporcional asignado a ese cliente
  //   - centroId activo  → costo proporcional asignado a ese centro
  //   - sin filtro       → costoGeneral completo
  const displayData = useMemo(() => {
    return filteredData.map((r) => {
      let efectivo;
      if (activeSearch.clienteId) {
        efectivo = r.clientes?.find(c => c.id === activeSearch.clienteId)?.costo ?? 0;
      } else if (activeSearch.centroId) {
        efectivo = r.centros?.find(c => c.id === activeSearch.centroId)?.costo ?? 0;
      } else {
        efectivo = r.costoGeneral || 0;
      }
      return { ...r, _efectivo: efectivo };
    });
  }, [filteredData, activeSearch]);

  const metrics = useMemo(() => {
    const total = displayData.reduce((s, r) => s + r._efectivo, 0);

    const porNivel = Object.fromEntries(
      ['empresa', 'centro', 'cliente'].map((n) => [
        n,
        displayData.filter((r) => r.nivel === n).reduce((s, r) => s + r._efectivo, 0),
      ])
    );

    const porCategoria = CATEGORIAS.reduce((acc, cat) => {
      const v = displayData.filter((r) => r.categoria === cat).reduce((s, r) => s + r._efectivo, 0);
      if (v > 0) acc[cat] = v;
      return acc;
    }, {});

    const catSorted = Object.entries(porCategoria).sort(([, a], [, b]) => b - a);

    const porMes = {};
    displayData.forEach((r) => {
      if (!r.fecha) return;
      const mes = r.fecha.slice(0, 7);
      porMes[mes] = (porMes[mes] || 0) + r._efectivo;
    });
    const mesSorted = Object.entries(porMes).sort(([a], [b]) => a.localeCompare(b));

    return { total, porNivel, catSorted, mesSorted };
  }, [displayData]);

  const handleExport = () => {
    if (!displayData.length) return;
    import('xlsx').then((XLSX) => {
      const rows = displayData.map((r) => ({
        Fecha: r.fecha ? r.fecha.slice(0, 10) : '',
        Descripción: r.descripcion || '',
        Categoría: r.categoria || '',
        Nivel: r.nivel || '',
        'Costo Asignado': r._efectivo,
        'Costo Total Registro': r.costoGeneral || 0,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Gastos');
      XLSX.writeFile(wb, `reporte-gastos-${fechaDesde}-${fechaHasta}.xlsx`);
    });
  };

  const costLabel = activeSearch.centroId || activeSearch.clienteId ? 'Costo Asignado' : 'Costo Total';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 space-y-4"
    >
      {/* ── Header ── */}
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-border/50">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/financiera')} className="shrink-0">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="bg-violet-500/15 p-2 rounded-lg">
              <BarChart3 className="h-6 w-6 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <CardTitle>Reportes Financieros</CardTitle>
              <CardDescription>Análisis de gastos por período, nivel y categoría</CardDescription>
            </div>
          </div>
          {displayData.length > 0 && (
            <Button variant="outline" onClick={handleExport} className="gap-2">
              <FileDown className="h-4 w-4" />
              Exportar Excel
            </Button>
          )}
        </CardHeader>

        {/* ── Filtros ── */}
        <CardContent className="pt-5 space-y-3">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Fecha desde</Label>
              <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Fecha hasta</Label>
              <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Nivel jerárquico</Label>
              <Select value={filterNivel || '_all'} onValueChange={(v) => setFilterNivel(v === '_all' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos</SelectItem>
                  <SelectItem value="empresa">Empresa (todos los costos)</SelectItem>
                  <SelectItem value="centro">Centro y clientes</SelectItem>
                  <SelectItem value="cliente">Solo clientes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Categoría</Label>
              <Select value={filterCategoria || '_all'} onValueChange={(v) => setFilterCategoria(v === '_all' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todas</SelectItem>
                  {CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Centro específico</Label>
              <Select
                value={filterCentroId || '_all'}
                onValueChange={(v) => setFilterCentroId(v === '_all' ? '' : v)}
                disabled={loadingCentros}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingCentros ? 'Cargando...' : 'Todos los centros'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos los centros</SelectItem>
                  {centros.map((c) => <SelectItem key={c._id} value={c._id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Cliente específico</Label>
              <Select
                value={filterClienteId || '_all'}
                onValueChange={(v) => setFilterClienteId(v === '_all' ? '' : v)}
                disabled={!filterCentroId || loadingClientes}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !filterCentroId ? 'Selecciona un centro primero' :
                    loadingClientes ? 'Cargando...' : 'Todos los clientes'
                  } />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos los clientes</SelectItem>
                  {clientes.map((c) => <SelectItem key={c._id} value={c._id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSearch} disabled={isLoading} className="gap-2">
              {isLoading
                ? <div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                : <Search className="h-4 w-4" />}
              Generar reporte
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Estado vacío ── */}
      {!hasSearched && (
        <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground gap-3">
          <BarChart3 className="h-12 w-12 opacity-30" />
          <p className="text-sm">Selecciona el período y presiona <strong>Generar reporte</strong></p>
        </div>
      )}

      {hasSearched && displayData.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground gap-3">
          <DollarSign className="h-12 w-12 opacity-30" />
          <p className="text-sm">No se encontraron registros para los filtros seleccionados</p>
        </div>
      )}

      {hasSearched && displayData.length > 0 && (
        <>
          {/* ── Resumen ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
              <Card className="bg-violet-500/10 border-violet-200 dark:border-violet-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4 text-violet-500" />
                    <span className="text-xs font-medium text-muted-foreground">Total período</span>
                  </div>
                  <p className="text-2xl font-bold tabular-nums">{fmt(metrics.total)}</p>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Layers className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">Registros</span>
                  </div>
                  <p className="text-2xl font-bold tabular-nums">{displayData.length}</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* ── Por nivel ── */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  Distribución por Nivel
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(NIVEL_CONFIG).map(([key, cfg]) => (
                  metrics.porNivel[key] > 0 && (
                    <BarRow
                      key={key}
                      label={cfg.label}
                      value={metrics.porNivel[key]}
                      total={metrics.total}
                      colorClass={cfg.color}
                      badge={
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cfg.light} ${cfg.text}`}>
                          {cfg.label}
                        </span>
                      }
                    />
                  )
                ))}
              </CardContent>
            </Card>

            {/* ── Por categoría ── */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  Distribución por Categoría
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {metrics.catSorted.map(([cat, val], i) => (
                  <BarRow
                    key={cat}
                    label={cat}
                    value={val}
                    total={metrics.total}
                    colorClass={CAT_COLORS[i % CAT_COLORS.length]}
                  />
                ))}
              </CardContent>
            </Card>
          </div>

          {/* ── Por mes ── */}
          {metrics.mesSorted.length > 1 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  Evolución Mensual
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {metrics.mesSorted.map(([mes, val]) => {
                  const [year, month] = mes.split('-');
                  const label = new Date(+year, +month - 1, 1).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
                  return (
                    <BarRow
                      key={mes}
                      label={label.charAt(0).toUpperCase() + label.slice(1)}
                      value={val}
                      total={Math.max(...metrics.mesSorted.map(([, v]) => v))}
                      colorClass="bg-violet-500"
                    />
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* ── Tabla detalle ── */}
          <Card>
            <CardHeader className="pb-3 border-b border-border/50">
              <CardTitle className="text-base">Detalle de Registros</CardTitle>
              <CardDescription>{displayData.length} registros en el período seleccionado</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Fecha</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Descripción</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Categoría</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Nivel</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">{costLabel}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayData.map((r, i) => {
                      const nCfg = NIVEL_CONFIG[r.nivel] || {};
                      return (
                        <tr key={r._id || i} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-2.5 tabular-nums text-muted-foreground whitespace-nowrap">
                            {r.fecha ? r.fecha.slice(0, 10) : '—'}
                          </td>
                          <td className="px-4 py-2.5 max-w-[200px] truncate">{r.descripcion || '—'}</td>
                          <td className="px-4 py-2.5">
                            <span className="text-xs bg-muted px-2 py-0.5 rounded-full">{r.categoria || '—'}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            {nCfg.label && (
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${nCfg.light} ${nCfg.text}`}>
                                {nCfg.label}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right font-semibold tabular-nums whitespace-nowrap">
                            {fmt(r._efectivo)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/40 border-t-2 border-border">
                      <td colSpan={4} className="px-4 py-2.5 font-semibold">Total</td>
                      <td className="px-4 py-2.5 text-right font-bold tabular-nums text-violet-700 dark:text-violet-400">
                        {fmt(metrics.total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </motion.div>
  );
};

export default ReportesFinancierosPage;
